// apps/client/src/world/decorate.ts (M1-T16) — decorate mode, a port of look-demo `decorate.ts` makeDecorator: a raycast onto the floor
// plane from pointer events, a translucent ghost with green/red emissive feedback, the footprint square under it, press a placed piece
// to lift it, drag to move it, let go to set it down. Footprints and validity come from the shared rules (footprintOf, surface.check),
// the piece floats tuning.decor.liftHeight above the floor, and setting down asks the server (commit) instead of saving locally: a
// rejected move puts the piece back where it was. Rooms and yards share it through DecorSurface (a yard has an origin offset).
//
// Pointer gestures on a placed piece (spec §9.9 "drag to move, tap to rotate, store it back"): a drag moves it; a quick tap turns it a
// quarter in place; a press held still keeps it lifted, so the Turn and Put-away controls (DESIGN.md) can act on it, and the next tap
// sets it down there. While decorate mode is active it owns the canvas's pointer input (no tap-to-walk, joystick or pinch under it).
import * as THREE from 'three';
import { tuning, type Rot } from '@whiskerton/content';
import type { ItemId, ItemView, SpaceId, TileXY, Vec2 } from '@whiskerton/shared';
import { footprintOf, type Placed } from '@whiskerton/shared/rules/placement';

export interface Held { item: ItemView; x: number; y: number; rot: Rot; from: { x: number; y: number; rot: Rot } | null }
export interface DecorSurface {
  kind: 'room' | 'yard'; space: SpaceId; origin: TileXY; size: { w: number; h: number };
  placed(): Placed[]; check(cand: Placed): string | null; objectFor(id: ItemId): THREE.Object3D | undefined;
  group: THREE.Object3D; grid: THREE.Object3D; foot: THREE.Mesh;
}
// DESIGN.md decorate feedback colours (presentation): leaf-700 valid, flag-500 blocked.
const GOOD = new THREE.Color('#3A7D44'), BAD = new THREE.Color('#E0301E');

export function snapToGrid(p: Vec2, foot: { w: number; d: number }, s: Pick<DecorSurface, 'origin' | 'size'>): TileXY {
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  return {
    x: clamp(Math.round(p.x - foot.w / 2), s.origin.x, s.origin.x + s.size.w - foot.w),
    y: clamp(Math.round(p.y - foot.d / 2), s.origin.y, s.origin.y + s.size.h - foot.d),
  };
}

type Sfx = 'lift' | 'place' | 'blocked' | 'turn' | 'stash';
type Lifted = Held & { obj: THREE.Object3D; mats: THREE.Material[] };
/** One press on the canvas: where and when it started, whether it dragged, whether it lifted a placed piece, and whether it ended
 *  (an end that arrives before the lifted piece's ghost has loaded is applied once it has). */
interface Press { x: number; y: number; t: number; moved: boolean; lift: boolean; up: boolean; long: boolean; cancelled: boolean }
const LISTEN = { capture: true } as const;
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
/** Decorate mode takes the press before the game's own canvas listeners (tap-to-walk, joystick, pinch). */
const own = (e: Event) => { (e as Partial<Event>).stopImmediatePropagation?.(); };

export function createDecorator(o: {
  camera: THREE.Camera; canvas: HTMLCanvasElement; surface: DecorSurface; ghost(item: ItemView): Promise<THREE.Object3D>;
  commit(h: Held): Promise<boolean>; stash(h: Held): Promise<boolean>; sfx(name: Sfx): void; onChange(h: Held | null, valid: boolean): void;
}): { enter(): void; exit(): void; take(item: ItemView): Promise<void>; rotate(): void; putAway(): Promise<void>; cancel(): void; readonly holding: boolean } {
  const s = o.surface;
  let active = false, busy = false, held: Lifted | null = null, press: Press | null = null, lifting = 0;
  const ray = new THREE.Raycaster(), ndc = new THREE.Vector2(), hit = new THREE.Vector3();
  const floor = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const last = { x: s.origin.x + s.size.w / 2, y: s.origin.y + s.size.h / 2 };
  /** Where the pointer held a lifted piece, from its centre: dragging keeps that grip, so pressing a piece never shifts it. */
  const grab = { x: 0, y: 0 };
  const groundY = () => { s.group.updateWorldMatrix(true, false); return new THREE.Vector3().setFromMatrixPosition(s.group.matrixWorld).y; };
  const pointer = (e: PointerEvent) => {
    const r = o.canvas.getBoundingClientRect();
    ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(ndc, o.camera);
  };
  /** The floor point under the pointer, in the surface's tile coordinates (x → x, z → y). */
  const onFloor = (): Vec2 | null => {
    floor.constant = -groundY();
    return ray.ray.intersectPlane(floor, hit) ? { x: hit.x, y: hit.z } : null;
  };
  const plain = (h: Lifted): Held => ({ item: h.item, x: h.x, y: h.y, rot: h.rot, from: h.from });
  const cand = (h: Held): Placed => ({ id: h.item.id, catalogId: h.item.catalogId, x: h.x, y: h.y, rot: h.rot });
  /** Translucent clones of the ghost's materials (never the shared originals), tinted by follow(); its contact blob stays on the floor. */
  const tint = (obj: THREE.Object3D) => {
    const mats: THREE.Material[] = [];
    obj.traverse((x) => {
      const m = x as THREE.Mesh;
      if (!m.isMesh) return;
      if (m.userData.blob) { m.position.y -= tuning.decor.liftHeight; return; }
      const lift = (src: THREE.Material) => {
        const c = src.clone(); c.transparent = true; c.opacity = 0.8;
        if ('emissive' in c) { (c as THREE.MeshStandardMaterial).emissive = GOOD.clone(); (c as THREE.MeshStandardMaterial).emissiveIntensity = 0.35; }
        mats.push(c); return c;
      };
      m.material = Array.isArray(m.material) ? m.material.map(lift) : lift(m.material);
      m.castShadow = true;
    });
    return mats;
  };
  const follow = (p: Vec2) => {
    if (!held) return;
    last.x = p.x; last.y = p.y;
    const fp = footprintOf({ catalogId: held.item.catalogId, rot: held.rot });
    const t = snapToGrid({ x: p.x - grab.x, y: p.y - grab.y }, fp, s);
    held.x = t.x; held.y = t.y;
    held.obj.position.set(t.x + fp.w / 2, tuning.decor.liftHeight, t.y + fp.d / 2);
    held.obj.rotation.y = -held.rot * Math.PI / 2;
    const bad = s.check(cand(held)) !== null;
    for (const m of held.mats) {
      if ('emissive' in m) { const sm = m as THREE.MeshStandardMaterial; sm.emissive.copy(bad ? BAD : GOOD); sm.emissiveIntensity = bad ? 0.75 : 0.32; }
      m.opacity = bad ? 0.6 : 0.82;
    }
    s.foot.visible = true; s.foot.scale.set(fp.w, 1, fp.d); s.foot.position.set(t.x + fp.w / 2, 0.05, t.y + fp.d / 2);
    (s.foot.material as THREE.MeshBasicMaterial).color.copy(bad ? BAD : GOOD);
    o.onChange(plain(held), !bad);
  };
  const release = () => {
    if (!held) return;
    held.obj.parent?.remove(held.obj);
    for (const m of held.mats) m.dispose();
    s.foot.visible = false; held = null;
    o.onChange(null, true);
  };
  /** Lets go of the ghost; a piece lifted from the floor shows again where it was. */
  const restore = () => { if (!held) return; const orig = held.from && s.objectFor(held.item.id); if (orig) orig.visible = true; release(); };
  const drop = async () => {
    if (!held || busy) return;
    const h = held;
    if (s.check(cand(h))) { o.sfx('blocked'); return; }                 // stays lifted: move it, turn it or put it away
    busy = true;
    const ok = await o.commit(plain(h)).catch(() => false);
    busy = false;
    if (held !== h) return;                                             // left decorate mode meanwhile (restore already ran)
    if (ok) { o.sfx('place'); const orig = s.objectFor(h.item.id); if (orig) orig.visible = true; release(); }   // the ack / item.* event moved it
    else { o.sfx('blocked'); restore(); }                               // a rejected move puts the piece back
  };
  /** The end of a press, once any piece it lifted is in hand. */
  const ended = (p: Press) => {
    if (!held || busy) return;
    if (p.cancelled) { if (p.lift) restore(); return; }
    if (p.lift && !p.moved) {
      if (p.long) return;                                               // held still: keep it lifted for Turn / Put away
      held.rot = ((held.rot + 1) % 4) as Rot; follow(last); o.sfx('turn');   // a tap turns it in place
    }
    void drop();
  };
  const onDown = (e: PointerEvent) => {
    if (!active) return;
    own(e);
    if (busy) return;
    pointer(e);
    const p: Press = { x: e.clientX, y: e.clientY, t: now(), moved: false, lift: false, up: false, long: false, cancelled: false };
    press = p;
    if (held) { grab.x = grab.y = 0; const f = onFloor(); if (f) follow(f); return; }   // a lifted piece comes to the pointer
    const hits = ray.intersectObjects(s.placed().map((q) => s.objectFor(q.id as ItemId)).filter((x): x is THREE.Object3D => !!x && x.visible), true);
    if (!hits.length) return;
    let top: THREE.Object3D | null = hits[0]!.object;
    while (top && top.userData.itemId === undefined) top = top.parent;
    const q = top && s.placed().find((x) => x.id === top!.userData.itemId);
    if (!top || !q) return;
    p.lift = true;
    const seq = ++lifting, at = onFloor();
    const item: ItemView = { id: q.id as ItemId, catalogId: q.catalogId, qty: 1, owner: null,
      at: s.kind === 'room' ? { loc: 'room', space: s.space, x: q.x, y: q.y, rot: q.rot } : { loc: 'outdoors', x: q.x, y: q.y, rot: q.rot, placed: true } };
    void (async () => {
      const obj = await o.ghost(item);
      if (seq !== lifting || !active || held) return;                   // superseded, or decorate mode ended while it loaded
      top!.visible = false;
      s.group.add(obj);
      held = { item, x: q.x, y: q.y, rot: q.rot, from: { x: q.x, y: q.y, rot: q.rot }, obj, mats: tint(obj) };
      o.sfx('lift');
      const fp = footprintOf(q), c = { x: q.x + fp.w / 2, y: q.y + fp.d / 2 };
      grab.x = at ? at.x - c.x : 0; grab.y = at ? at.y - c.y : 0;
      follow(at ?? c);
      if (p.up) ended(p);
    })();
  };
  /** Only a press drags: a mouse hovering on its way to the Turn / Put-away controls (which float above the piece) never moves it. */
  const onMove = (e: PointerEvent) => {
    if (!active || !press) return;
    if (!press.moved && Math.hypot(e.clientX - press.x, e.clientY - press.y) > tuning.input.tapMaxPx) press.moved = true;
    if (!held || busy) return;
    pointer(e);
    const f = onFloor(); if (f) follow(f);
  };
  const finish = (cancelled: boolean) => (e: PointerEvent) => {
    if (!active || !press) return;
    own(e);
    const p = press; press = null;
    p.up = true; p.long = now() - p.t > tuning.input.tapMaxMs; p.cancelled = cancelled;
    if (p.lift && !held) return;                                        // the ghost is still loading: it ends the press when it lands
    ended(p);
  };
  const onUp = finish(false), onCancel = finish(true);
  return {
    enter() {
      active = true; s.grid.visible = true;
      o.canvas.addEventListener('pointerdown', onDown, LISTEN); o.canvas.addEventListener('pointermove', onMove, LISTEN);
      o.canvas.addEventListener('pointerup', onUp, LISTEN); o.canvas.addEventListener('pointercancel', onCancel, LISTEN);
    },
    exit() {
      restore(); lifting++; press = null; active = false; s.grid.visible = false; s.foot.visible = false;
      o.canvas.removeEventListener('pointerdown', onDown, LISTEN); o.canvas.removeEventListener('pointermove', onMove, LISTEN);
      o.canvas.removeEventListener('pointerup', onUp, LISTEN); o.canvas.removeEventListener('pointercancel', onCancel, LISTEN);
    },
    async take(item: ItemView) {
      if (busy) return;
      restore();
      const seq = ++lifting;
      const obj = await o.ghost(item);
      if (seq !== lifting || !active) return;
      restore();
      s.group.add(obj);
      held = { item, x: 0, y: 0, rot: 0, from: null, obj, mats: tint(obj) };
      grab.x = grab.y = 0;
      o.sfx('lift'); follow(last);
    },
    rotate() { if (held && !busy) { held.rot = ((held.rot + 1) % 4) as Rot; follow(last); o.sfx('turn'); } },
    async putAway() {
      if (!held || busy) return;
      const h = held;
      if (!h.from) { o.sfx('stash'); release(); return; }               // never placed: just let go
      busy = true;
      const ok = await o.stash(plain(h)).catch(() => false);
      busy = false;
      if (held !== h) return;
      if (ok) { o.sfx('stash'); release(); } else restore();            // the ack / item.removed event takes the piece away
    },
    cancel: restore,
    get holding() { return held !== null; },
  };
}
